import { useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import { generateWeekSnapshot } from '../services/groceryListService';
import { buildGroceryList, setStale, selectGroceryWeekStart, selectGroceryRange } from '../redux/groceryListSlice';
import { selectMealsForDateRange, selectMealPlanHydrated } from '../redux/mealPlanSlice';
import { PlannedMeal } from '../redux/mealPlanSlice';
import { isRecipeMeal } from '../redux/mealEntry';
import { useToday } from './useToday';

const DEBOUNCE_MS = 2000;

/**
 * Pure function to check if the grocery list is stale.
 * Extracted for testability.
 *
 * Hashes recipe meals only, exactly like `buildGroceryList` does when it writes
 * `weekSnapshot`. Both sides MUST cover the same meal set: when they didn't,
 * any week holding a non-recipe meal was permanently stale and the effect
 * re-dispatched generation forever on an idle tab (#548).
 */
export function checkGroceryListStaleness(
  currentWeekMeals: PlannedMeal[],
  storedSnapshot: string,
  generatedDate: string | null,
  planHydrated = false,
): {
  isStale: boolean;
  shouldRegenerate: boolean;
  currentSnapshot: string;
} {
  if (currentWeekMeals.length === 0) {
    // An empty range is usually a plan that has not loaded yet, and rebuilding
    // then would empty the list and sync the empty list to the household. Once
    // the plan has loaded, it means the list's days are over or cleared (#804:
    // a Thursday after a Monday-to-Wednesday plan), and a list built from
    // recipes is stale: it empties down to its custom and checked items.
    if (planHydrated && storedSnapshot !== '') {
      const emptySnapshot = generateWeekSnapshot([]);
      if (storedSnapshot !== emptySnapshot) {
        return { isStale: true, shouldRegenerate: true, currentSnapshot: emptySnapshot };
      }
    }
    return { isStale: false, shouldRegenerate: false, currentSnapshot: '' };
  }

  // Only recipe meals contribute ingredients. A week that still has entries but
  // no recipes hashes as an empty set, so deleting the last recipe correctly
  // re-stales the list rather than leaving its ingredients behind.
  const currentSnapshot = generateWeekSnapshot(currentWeekMeals.filter(isRecipeMeal));

  // Stale if snapshot differs from stored
  const isStale = currentSnapshot !== storedSnapshot;

  // Should regenerate if stale OR if no list has been generated yet
  const shouldRegenerate = isStale || generatedDate === null;

  return { isStale, shouldRegenerate, currentSnapshot };
}

/**
 * Hook that watches the grocery list's own week and the inventory, detects when
 * the list is stale, and triggers debounced regeneration.
 *
 * Deliberately keyed on the list's PINNED week (`selectGroceryWeekStart`), not
 * on the week the planner happens to be showing. Browsing the planner is a
 * read-only act: it must not schedule work and must not rewrite the list. Only
 * an explicit "shop for this week" action repoints the list, via
 * `buildGroceryList({ weekStart })`.
 *
 * The 2-second debounce absorbs rapid edits (e.g. swapping recipes).
 */
export function useGroceryListSync(): void {
  const dispatch = useDispatch<AppDispatch>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inventoryFingerprintRef = useRef<string | null>(null);
  const lastGeneratedKeyRef = useRef<string | null>(null);

  const groceryWeekStart = useSelector(selectGroceryWeekStart);
  // The same range `buildGroceryList` reads: the pinned week from today on
  // (#804), stretched to `coversUntil` (#722). Both sides must hash the same
  // meals (#548). `today` is what makes a new day drop yesterday's recipes:
  // nothing in the plan or the pantry changes at midnight.
  const today = useToday();
  const { start: rangeStart, end: rangeEnd } = useSelector(
    (state: RootState) => selectGroceryRange(state, today),
    shallowEqual,
  );
  const groceryWeekMeals = useSelector((state: RootState) => selectMealsForDateRange(state, rangeStart, rangeEnd));
  const inventory = useSelector((state: RootState) => state.inventory?.items ?? []);
  const storedSnapshot = useSelector((state: RootState) => state.groceryList?.weekSnapshot ?? '');
  const generatedDate = useSelector((state: RootState) => state.groceryList?.generatedDate ?? null);
  const storedIsStale = useSelector((state: RootState) => state.groceryList?.isStale ?? false);
  const planHydrated = useSelector(selectMealPlanHydrated);

  // Pantry is the source of truth: corrections (add/remove an inventory item)
  // must reflow into the list, so track an active-inventory fingerprint too.
  const inventoryFingerprint = inventory
    .filter(item => !item.status || item.status === 'active')
    .map(item => item.id)
    .sort()
    .join('|');

  useEffect(() => {
    const { isStale, shouldRegenerate, currentSnapshot } = checkGroceryListStaleness(
      groceryWeekMeals,
      storedSnapshot,
      generatedDate,
      planHydrated,
    );

    const inventoryChanged =
      inventoryFingerprintRef.current !== null &&
      inventoryFingerprintRef.current !== inventoryFingerprint &&
      groceryWeekMeals.length > 0;
    inventoryFingerprintRef.current = inventoryFingerprint;

    // Backstop against a regeneration loop (#548): everything generation reads
    // is captured by this key, so once we have actually generated for it and a
    // list exists, running again cannot produce a different result. Any
    // staleness that survives it is a snapshot bug, not pending work.
    // Recorded when the debounced dispatch fires, never when it is merely
    // scheduled — a cancelled generation must not count as done.
    const regenerationKey = `${groceryWeekStart}|${rangeStart}|${rangeEnd}|${currentSnapshot}|${inventoryFingerprint}`;
    const alreadyGenerated = generatedDate !== null && lastGeneratedKeyRef.current === regenerationKey;

    // Keep the "Tap to refresh" badge honest in BOTH directions. `isStale` is
    // persisted, so a flag left over from an earlier session (notably the
    // builds affected by #548) would otherwise stick until the next real plan
    // change, even though the list is provably up to date.
    if (isStale !== storedIsStale) {
      dispatch(setStale(isStale));
    }

    if ((shouldRegenerate || inventoryChanged) && !alreadyGenerated) {
      // Cancel any pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the regeneration to absorb rapid meal edits
      debounceRef.current = setTimeout(() => {
        lastGeneratedKeyRef.current = regenerationKey;
        dispatch(buildGroceryList());
      }, DEBOUNCE_MS);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    groceryWeekStart,
    rangeStart,
    rangeEnd,
    groceryWeekMeals,
    inventoryFingerprint,
    storedSnapshot,
    generatedDate,
    storedIsStale,
    planHydrated,
    dispatch,
  ]);
}
